import React, { useState } from "react";
import { Pagination } from "@mui/material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG } from "@/types";
import { cn } from "@/lib/utils";

interface Column {
  title: string;
  field: string;
  render?: (rowData: any) => React.ReactNode;
  hideOnMobile?: boolean;
}

interface TableComponentProps {
  columns: Column[];
  data: any[];
  onRowClick?: (row: any) => void;
  rowKeyField: string;
  maxHeight?: number;
  isMobile?: boolean;
  rowsPerPage?: number;
  pagination?: boolean;
}

const TableComponent: React.FC<TableComponentProps> = ({
  columns,
  data,
  onRowClick,
  rowKeyField,
  maxHeight = 300,
  rowsPerPage = 10,
  pagination = false,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const paginatedData = pagination
    ? data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    : data;
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };
  return (
    <div
      className="bg-white overflow-x-auto no-scrollbar py-4 rounded-xl mx-4 w-full md:px-10 relative"
      style={{ direction: locale === LANG.AR ? "rtl" : "ltr" }}
    >
      <div
        className={`border-b border-gray-200 grid`}
        style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
      >
        {columns.map((column, index) => (
          <div
            key={index}
            className={cn(
              "p-4 min-w-36 text-center font-bold",
              column.field === "checkbox" && "min-w-16"
            )}
          >
            {column.title}
          </div>
        ))}
      </div>

      <div style={{ maxHeight, padding: "8px" }}>
        {paginatedData.length > 0 ? (
          paginatedData?.map((row) => (
            <div
              key={row[rowKeyField]}
              className="grid items-center cursor-pointer hover:scale-[1.01] border-green-300 !rounded-2xl"
              style={{
                gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
              }}
              onClick={() => onRowClick?.(row)}
            >
              {columns?.map((column, index) => (
                <div
                  key={index}
                  className={cn(
                    "p-4 min-w-36 text-center",
                    column.field === "checkbox" && "min-w-20"
                  )}
                >
                  {column.render ? column.render(row) : row[column.field]}
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="text-center p-4">{t("general.NoDataAvailable")}</div>
        )}
      </div>

      {pagination && totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                "&.Mui-selected": {
                  backgroundColor: "#644980",
                  color: "#fff",
                },
                "&:hover": {
                  backgroundColor: "#644980",
                  color: "#fff",
                },
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TableComponent;
